import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo, reviews } from '@/mocks/reviews';

const FORM_URL = 'http://uyeqdpvovrg.cn/';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState<string>('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    fetch(FORM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed: Record<string, unknown> | null = null;
        try { parsed = JSON.parse(text); } catch { /* ignore */ }
        const serverMsg = (parsed as Record<string, unknown>)?.meta as Record<string, unknown>;
        const msgStr = serverMsg?.message as string || '';
        if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(msgStr || '提交失败，请稍后重试。');
        }
      })
      .catch(() => {
        setFormStatus('error');
        setFormError('网络错误，请稍后重试。');
      });
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫之家',
    description: '了解韩漫之家品牌故事、用户口碑与联系方式。',
    url: 'http://uyeqdpvovrg.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'http://uyeqdpvovrg.cn/',
    },
    about: {
      '@type': 'Organization',
      name: aboutInfo.brand.name,
      description: aboutInfo.brand.mission,
      foundingDate: '2020',
      url: 'http://uyeqdpvovrg.cn/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '韩漫之家是完全免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的，韩漫之家目前所有漫画内容均完全免费提供，无需付费即可畅享海量韩漫资源。' } },
      { '@type': 'Question', name: '漫画的更新频率是怎样的？', acceptedAnswer: { '@type': 'Answer', text: '不同漫画的更新频率不同，大部分热门作品保持每周更新2-3次。' } },
      { '@type': 'Question', name: '为什么有些漫画章节打不开？', acceptedAnswer: { '@type': 'Answer', text: '如果遇到章节无法打开的情况，建议刷新页面或稍后再试。如果问题持续存在，可以通过联系我们页面反馈。' } },
      { '@type': 'Question', name: '可以在手机上阅读吗？', acceptedAnswer: { '@type': 'Answer', text: '韩漫之家全面适配移动端，支持手机和平板设备，无需下载任何App。' } },
      { '@type': 'Question', name: '如何找到我想看的漫画类型？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过首页的分类导航浏览各个类型的漫画，也可以使用搜索功能直接查找作品名称。' } },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫之家 - 免费韩漫阅读平台"
        description="了解韩漫之家品牌故事——免费韩漫大全平台，支持韩国漫画下拉式在线阅读。查看用户口碑评价与联系方式，加入数百万韩漫爱好者社区。"
        keywords="韩漫之家,关于我们,韩漫平台,免费韩漫"
        canonicalUrl="http://uyeqdpvovrg.cn/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 text-center">
              关于 <span className="text-primary-500">韩漫之家</span>
            </h1>
            <p className="mt-3 text-sm text-foreground-500 text-center">{aboutInfo.brand.slogan}</p>
          </div>

          <div className="max-w-4xl mx-auto mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {aboutInfo.stats.map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100">
                <p className="text-2xl md:text-3xl font-bold font-heading text-primary-500">{stat.value}</p>
                <p className="mt-2 text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto mt-14">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl font-semibold text-foreground-900 mb-4">品牌故事</h2>
                <div className="bg-background-100 rounded-lg p-6">
                  <p className="text-sm text-foreground-700 leading-relaxed">{aboutInfo.brand.story}</p>
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-background-200/70">
                    <span className="text-xs text-foreground-500">创立时间：{aboutInfo.brand.founded}</span>
                    <span className="text-xs text-foreground-500">使命：{aboutInfo.brand.mission}</span>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-foreground-900 mb-4">读者口碑</h2>
                <div className="space-y-4">
                  {reviews.slice(0, 3).map((review) => (
                    <div key={review.id} className="bg-background-100 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                          <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900 truncate">{review.name}</p>
                          <p className="text-xs text-foreground-500">{review.role}</p>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-primary-400 text-xs"></i>
                          ))}
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mt-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div id="contact">
                <h2 className="text-xl font-semibold text-foreground-900 mb-4">联系我们</h2>
                <div className="bg-background-100 rounded-lg p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm font-medium text-foreground-900">hello@uyeqdpvovrg.cn</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-qq-line text-accent-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">客服QQ</p>
                        <p className="text-sm font-medium text-foreground-900">800123456</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-secondary-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">工作时间</p>
                        <p className="text-sm font-medium text-foreground-900">每日 9:00 - 22:00</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-foreground-900 mb-4">在线留言</h2>
                <form
                  id="contact-form"
                 
                  onSubmit={handleSubmit}
                  className="bg-background-100 rounded-lg p-6"
                >
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-msg" className="block text-sm font-medium text-foreground-700 mb-1">留言</label>
                      <textarea
                        id="contact-msg"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200 resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-hp"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">留言已成功提交，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-primary-600 text-center">{formError || '提交失败，请稍后重试。'}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mt-16">
            <h2 className="text-xl font-semibold text-foreground-900 mb-6">常见问题</h2>
            <div className="space-y-3">
              {[
                { q: '韩漫之家是完全免费的吗？', a: '是的，韩漫之家目前所有漫画内容均完全免费提供，无需付费即可畅享海量韩漫资源。我们通过广告收入维持运营，致力于为广大韩漫爱好者提供优质的阅读体验。' },
                { q: '漫画的更新频率是怎样的？', a: '不同漫画的更新频率不同，大部分热门作品保持每周更新2-3次。每部漫画的详情页都会标注具体的更新日期，您可以关注喜欢的作品以获取最新更新通知。' },
                { q: '为什么有些漫画章节打不开？', a: '如果遇到章节无法打开的情况，可能是由于网络波动或服务器临时维护。建议您刷新页面或稍后再试。如果问题持续存在，可以通过联系我们页面反馈，我们会在第一时间处理。' },
                { q: '可以在手机上阅读吗？', a: '当然可以！韩漫之家全面适配移动端，支持手机和平板设备。您可以直接在浏览器中访问我们的网站，享受流畅的下拉式阅读体验，无需下载任何App。' },
                { q: '如何找到我想看的漫画类型？', a: '您可以通过首页的分类导航浏览各个类型的漫画，也可以使用搜索功能直接查找作品名称。另外，排行榜和热门推荐也是发现好作品的好方式。' },
              ].map((faq, i) => (
                <details key={i} className="bg-background-100 rounded-lg overflow-hidden group">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors duration-200">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
            <script
              type="application/ld+json"
              data-schema="faq"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}